import _ from 'lodash';
import GENERAL_FUNCTIONS from './functions';
import rules from '../constants/rules';
import moment from 'moment';
import currencies from '../constants/currencies';
import i18n from '../../i18n/i18n';
import { store } from '../../store/store';

const questionFunctions = {};

questionFunctions.mapQuestionTypeCodeToDateInputProps = (
    questionTypeCode,
    question,
) => {
    if (questionTypeCode === 'date') {
        return {
            dateFormat: 'dd/MM/yyyy',
            showYearDropdown: true,
            showMonthDropdown: true,
            dropdownMode: 'select',
            name: question.code,
        };
    }

    if (questionTypeCode === 'date-time') {
        return {
            dateFormat: 'MMMM d, yyyy h:mm aa',
            showYearDropdown: true,
            showMonthDropdown: true,
            showTimeSelect: true,
            dropdownMode: 'select',
            name: question.code,
        };
    }

    if (questionTypeCode === 'time') {
        return {
            dateFormat: 'h:mm aa',
            showTimeSelect: true,
            showTimeSelectOnly: true,
            dropdownMode: 'select',
            name: question.code,
        };
    }

    return null;
};

questionFunctions.mapQuestionTypeCodeToAsyncSearchableListInputProps =
    questionTypeCode => {
        if (questionTypeCode === 'post-code') {
            return {
                noOptionsMessage: () => {
                    return 'No Results';
                },
                loadingMessage: () => null,
                placeholder: '',
                noOptionsText: 'No results found',
            };
        }

        return null;
    };

questionFunctions.constructAnswerDto = (
    question,
    answer,
    questionType,
    restOptions,
) => {
    if (
        questionType.code === 'text-input' ||
        questionType.code === 'text-area' ||
        questionType.code === 'post-code'
    ) {
        return {
            QuestionCode: question.code,
            Text: answer,
            Amount: null,
            Date: null,
            OptionId: null,
            Flag: null,
            QuestionId: question.id,
        };
    }

    if (questionType.code === 'amount') {
        return {
            QuestionCode: question.code,
            Text: null,
            Amount: parseFloat(
                typeof answer === 'string' ? answer.replace(',', '.') : answer,
            ),
            CurrencyId: restOptions?.currency?.id ?? null,
            Date: null,
            OptionId: null,
            Flag: null,
            QuestionId: question.id,
        };
    }

    if (questionType.code === 'number') {
        return {
            QuestionCode: question.code,
            Text: null,
            Amount: parseFloat(answer),
            Date: null,
            OptionId: null,
            Flag: null,
            QuestionId: question.id,
        };
    }

    if (
        questionType.code === 'date' ||
        questionType.code === 'date-time' ||
        questionType.code === 'time'
    ) {
        return {
            QuestionCode: question.code,
            Text: null,
            Amount: null,
            Date: answer ? moment(answer).format('YYYY-MM-DD') : null,
            OptionId: null,
            Flag: null,
            QuestionId: question.id,
        };
    }

    if (questionType.code === 'list') {
        return {
            QuestionCode: question.code,
            Text: null,
            Amount: null,
            Date: null,
            OptionId: answer && answer.trim().length > 0 ? answer : null,
            Flag: null,
            QuestionId: question.id,
        };
    }

    if (questionType.code === 'checkbox') {
        return {
            QuestionCode: question.code,
            Text: null,
            Amount: null,
            Date: null,
            OptionId: null,
            Flag: answer && answer.length > 0,
            QuestionId: question.id,
        };
    }

    if (questionType.code === 'attachment') {
        return {
            QuestionCode: question.code,
            Text: null,
            Amount: null,
            Date: null,
            OptionId: null,
            Flag: null,
            Attachments:
                answer && answer.length
                    ? answer.map(attachment => ({
                          id: attachment.id,
                          documentTypeCodes:
                              attachment.documentTypes &&
                              attachment.documentTypes.length
                                  ? attachment.documentTypes.map(
                                        type => type.value,
                                    )
                                  : [],
                      }))
                    : [],
            QuestionId: question.id,
        };
    }

    return {
        QuestionCode: question.code,
        Text: answer,
        Amount: null,
        Date: null,
        OptionId: null,
        Flag: null,
        QuestionId: question.id,
    };
};

questionFunctions.requiredQuestionByOptionId = (
    question,
    values,
    questions,
) => {
    let valuesArrays = Object.values(values);
    let parentQuestion = Object.values(questions).find(
        value => value.id === question.requiredByQuestionId,
    );
    return (
        !!(
            question.requiredByOptionId &&
            valuesArrays.find(value => value === question.requiredByOptionId)
        ) &&
        question.requiredByQuestionId === parentQuestion.id &&
        values[parentQuestion.code]
    );
};

questionFunctions.validateAttachment = (attachment, question) => {
    if (
        attachment &&
        (!attachment.documentTypes || attachment.documentTypes.length === 0)
    ) {
        return i18n.t('requiredField');
    }
    if (
        attachment &&
        attachment.documentTypes &&
        attachment.documentTypes.length > 0 &&
        question &&
        question.documentTypes.some(item => item.isRequired)
    ) {
        return i18n.t('mandatoryDocType');
    }
    return null;
};

questionFunctions.mapFormAnswersToDTOAnswers = (
    questions,
    questionTypes,
    answers,
    currency,
) => {
    // construct answer dtos from the form values
    const formAnswers = Object.keys(answers).map(key => {
        const answer = answers[key];
        const answerQuestion = questions[key];
        const questionType = questionTypes[answerQuestion.questionTypeId];
        return questionFunctions.constructAnswerDto(
            answerQuestion,
            answer,
            questionType,
            { currency: currency },
        );
    });

    // Get the ID of the checkbox question type
    const checkboxQuestionTypeId = _.find(
        questionTypes,
        type => type.code === 'checkbox',
    ).id;

    // Filter the required checkbox questions
    const requiredCheckboxQuestions = _.filter(questions, question => {
        const isRequiredCheckboxQuestion =
            question.isRequired &&
            question.questionTypeId === checkboxQuestionTypeId;
        const isParentOrEnabled =
            question.isParent ||
            formAnswers.some(
                answer =>
                    answer.OptionId === question.enabledByOptionId &&
                    question.enabledByQuestionId === answer.QuestionId,
            );
        const hasAnswerInFormAnswers = formAnswers.some(
            answer => answer.QuestionCode === question.code,
        );

        return (
            isRequiredCheckboxQuestion &&
            isParentOrEnabled &&
            !hasAnswerInFormAnswers
        );
    });

    // add the required unanswered checkbox questions as false answers
    const checkBoxQuestionAnswers = requiredCheckboxQuestions.map(question => {
        const questionType = questionTypes[question.questionTypeId];
        return questionFunctions.constructAnswerDto(
            question,
            false,
            questionType,
            { currency: currency },
        );
    });

    return [...formAnswers, ...checkBoxQuestionAnswers];
};

questionFunctions.constructAnswersDTO = (
    questions,
    questionTypes,
    answers,
    currency,
    draftAnswers,
) => {
    const dtoAnswers = questionFunctions.mapFormAnswersToDTOAnswers(
        questions,
        questionTypes,
        answers,
        currency,
    );

    const mappedValues =
        draftAnswers && draftAnswers.length
            ? draftAnswers
                  .filter(e => !questions[e.questionCode])
                  .map(item => ({
                      QuestionCode: item.questionCode,
                      Text: item.text,
                      Amount: item.amount,
                      Currency:
                          currency && currency.code
                              ? currency.code.toUpperCase()
                              : item.currency,
                      Date: item.date,
                      OptionId: item.optionId,
                      Flag: item.flag,
                      Attachments:
                          item.attachments && item.attachments.length > 0
                              ? item.attachments
                              : [],
                      QuestionId: item.questionId,
                  }))
            : [];

    return GENERAL_FUNCTIONS.mergeArraysOfObjects(
        'QuestionCode',
        mappedValues,
        dtoAnswers,
    );
};

questionFunctions.isQuestionOfTypeIntegerRange = (
    question,
    questionTypeCode,
) => {
    if (!question) {
        return false;
    }

    return (
        questionTypeCode === 'number' &&
        question.questionValidationRules &&
        question.questionValidationRules[rules.integer.code] &&
        question.questionValidationRules[rules.minValue.code] &&
        question.questionValidationRules[rules.minValue.code].length === 1 &&
        question.questionValidationRules[rules.maxValue.code] &&
        question.questionValidationRules[rules.maxValue.code].length === 1
    );
};

questionFunctions.getQuestionIntegerRange = question => {
    const range = { minValue: null, maxValue: null };
    if (
        !question ||
        !question.questionValidationRules ||
        !question.questionValidationRules[rules.minValue.code] ||
        question.questionValidationRules[rules.minValue.code].length !== 1 ||
        !question.questionValidationRules[rules.maxValue.code] ||
        question.questionValidationRules[rules.maxValue.code].length !== 1
    ) {
        return null;
    }

    range.minValue =
        question.questionValidationRules[rules.minValue.code][0].value;
    range.maxValue = question.questionValidationRules[rules.maxValue.code][0]
        .value
        ? parseInt(
              question.questionValidationRules[rules.maxValue.code][0].value,
          )
        : question.questionValidationRules[rules.maxValue.code][0].value;

    return range;
};

questionFunctions.getQuestionValidationRuleMinAndMaxValue = (
    question,
    ruleCode,
    questionsAnswers = {},
) => {
    if (!question) {
        return { maxRestrictionValue: null, minRestrictionValue: null };
    }

    const valueRestrictions = question.questionValidationRules[ruleCode]
        ? question.questionValidationRules[ruleCode].map(rule => rule.value)
        : [];

    if (valueRestrictions.length === 0) {
        return { maxRestrictionValue: null, minRestrictionValue: null };
    }

    const maxRestrictionValue = valueRestrictions.reduce((acc, curr) => {
        // check if validation rule is related to another question answer
        let restriction;
        if (questionsAnswers[curr]) {
            restriction = questionsAnswers[curr];
        } else {
            restriction = curr;
        }

        if (acc === null) {
            return restriction;
        }

        return restriction > acc ? restriction : acc;
    }, null);

    const minRestrictionValue = valueRestrictions.reduce((acc, curr) => {
        // check if validation rule is related to another question answer
        let restriction;
        if (questionsAnswers[curr]) {
            restriction = questionsAnswers[curr];
        } else {
            restriction = curr;
        }

        if (acc === null) {
            return restriction;
        }

        return restriction < acc ? restriction : acc;
    }, null);

    return { maxRestrictionValue, minRestrictionValue };
};

// return the min and max dates allowed for question based on its
// validation rules. It will search every validation rule of the question
questionFunctions.dateQuestionRestrictions = (question, values = {}) => {
    let minDate = null;
    let maxDate = null;

    if (!question || !question.questionValidationRules) {
        return { minDate, maxDate };
    }

    Object.entries(question.questionValidationRules).forEach(
        ([ruleCode, rule]) => {
            let min, max;

            // get question answer restrictions values
            const { maxRestrictionValue, minRestrictionValue } =
                questionFunctions.getQuestionValidationRuleMinAndMaxValue(
                    question,
                    ruleCode,
                    values,
                );

            // handle future date validation rule
            if (
                ruleCode.toLowerCase() === rules.futureDate.code.toLowerCase()
            ) {
                min = moment().add(1, 'day').toDate();
                max = null;
            }

            //handle past-date validation rule
            if (ruleCode.toLowerCase() === rules.pastDate.code.toLowerCase()) {
                min = null;
                max = moment().subtract(1, 'day').toDate();
            }

            // handle today - future date validation rule
            if (
                ruleCode.toLowerCase() ===
                rules.todayFutureDate.code.toLowerCase()
            ) {
                min = new Date();
                max = null;
            }

            // handle today - past date validation rule
            if (
                ruleCode.toLowerCase() ===
                rules.todayPastDate.code.toLowerCase()
            ) {
                min = null;
                max = new Date();
            }

            // handle specific - equal or past date related to another question date validation rule
            if (
                values &&
                ruleCode.toLowerCase() ===
                    rules.beforeEqualSpecificDate.code.toLowerCase() &&
                minRestrictionValue
            ) {
                min = null;
                max = moment(minRestrictionValue).toDate();
            }

            // handle  past date validation rule
            if (
                ruleCode.toLowerCase() ===
                    rules.beforeSpecificDate.code.toLowerCase() &&
                values &&
                minRestrictionValue
            ) {
                min = null;
                max = moment(minRestrictionValue).subtract(1, 'day').toDate();
            }

            // handle future date validation rule
            if (
                ruleCode.toLowerCase() ===
                    rules.afterSpecificDate.code.toLowerCase() &&
                values &&
                maxRestrictionValue
            ) {
                min = moment(maxRestrictionValue).add(1, 'day').toDate();
                max = null;
            }

            // handle specific - future date validation rule
            if (
                ruleCode.toLowerCase() ===
                    rules.afterEqualSpecificDate.code.toLowerCase() &&
                values &&
                maxRestrictionValue
            ) {
                min = moment(maxRestrictionValue).toDate();
                max = null;
            }
            // handle specific - past date validation rule
            if (
                ruleCode.toLowerCase() ===
                    rules.pastSpecificDate.code.toLowerCase() &&
                values &&
                minRestrictionValue
            ) {
                min = null;
                max = moment()
                    .add(parseInt(minRestrictionValue), 'day')
                    .toDate();
            }
            // handle specific - future date validation rule
            if (
                ruleCode.toLowerCase() ===
                    rules.futureSpecificDate.code.toLowerCase() &&
                values &&
                maxRestrictionValue
            ) {
                min = moment()
                    .add(parseInt(maxRestrictionValue), 'day')
                    .toDate();
                max = null;
            }

            // update the global minimum and maximum acceptable values
            if (min && !minDate) {
                minDate = min;
            } else if (min && minDate && moment(min).isBefore(minDate, 'day')) {
                minDate = min;
            }

            if (max && !maxDate) {
                maxDate = max;
            } else if (max && maxDate && moment(max).isAfter(maxDate, 'day')) {
                maxDate = max;
            }
        },
    );

    return { minDate, maxDate };
};

questionFunctions.parseQuestionWithAnswer = (
    question,
    answer,
    questionType,
    currencyOptions,
) => {
    if (!answer) {
        return {
            question: {
                ...question,
                _questionTypeCode: questionType.code,
            },
            answer: {
                ...answer,
                _parsedAnswerTextValue: null,
            },
        };
    }

    if (
        questionType.code === 'text-input' ||
        questionType.code === 'text-area' ||
        questionType.code === 'post-code'
    ) {
        return {
            question: {
                ...question,
                _questionTypeCode: questionType.code,
            },
            answer: {
                ...answer,
                _parsedAnswerTextValue: answer.text,
            },
        };
    }

    if (questionType.code === 'amount') {
        const currency = currencyOptions?.find(c => c.id === answer.currencyId);

        const val = GENERAL_FUNCTIONS.appendCurrencySymbolAndFormat(
            answer.amount,
            currency?.symbol,
        );
        return {
            question: {
                ...question,
                _questionTypeCode: questionType.code,
            },
            answer: {
                ...answer,
                _parsedAnswerTextValue: val,
            },
        };
    }

    if (questionType.code === 'number') {
        return {
            question: {
                ...question,
                _questionTypeCode: questionType.code,
            },
            answer: {
                ...answer,
                _parsedAnswerTextValue: answer.amount,
            },
        };
    }

    if (
        questionType.code === 'date' ||
        questionType.code === 'date-time' ||
        questionType.code === 'time'
    ) {
        return {
            question: {
                ...question,
                _questionTypeCode: questionType.code,
            },
            answer: {
                ...answer,
                _parsedAnswerTextValue: moment(answer.date).format(
                    'DD/MM/YYYY',
                ),
            },
        };
    }

    if (questionType.code === 'list') {
        const selectedOption = question.options.find(
            o => o.id === answer.optionId,
        );
        return {
            question: {
                ...question,
                _questionTypeCode: questionType.code,
            },
            answer: {
                ...answer,
                _parsedAnswerTextValue: selectedOption
                    ? selectedOption.name
                    : null,
            },
        };
    }

    if (questionType.code === 'checkbox') {
        return {
            question: {
                ...question,
                _questionTypeCode: questionType.code,
            },
            answer: {
                ...answer,
                _parsedAnswerTextValue: answer.flag ? 'yes' : 'no',
            },
        };
    }

    return {
        question,
        answer,
    };
};

export default questionFunctions;
